import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import { HoldingClass, Partner } from 'types';
import { isShareClassVisible } from 'modules/trading/utils/isShareClassVisible';
import {
  TradingOffer,
  TradingOfferType,
} from '../../../../../types/TradingOffer';
import tradingOfferStatus from '../../../constants/tradingOfferStatus';
import TradingOfferPartialityIcon from './TradingOfferPartialityIcon';
import TradingOfferStatusBadge from '../../../components/TradingOfferStatusBadge';
import { getOfferStatus } from '../../../utils/getOfferStatus';

type TradingOfferRowProps = {
  offer: TradingOffer;
  onViewDetail: (offerId: string) => void; // eslint-disable-line no-unused-vars
};

const rowBorderColor = {
  [TradingOfferType.SELL]: 'border-left-2 border-left-success',
  [TradingOfferType.BUY]: 'border-left-2 border-left-primary',
};

const TradingOfferRow: FC<TradingOfferRowProps> = ({ offer, onViewDetail }) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === 'SELL';

  const partnerName = useMemo(() => {
    if (offer.isSellerNameHidden && offer.type === 'SELL')
      return t('HiddenName');
    if (typeof offer.partner === 'string') {
      return '';
    }
    return (offer.partner as Partner)?.name || '';
  }, [offer.partner, offer.isSellerNameHidden, offer.type, t]);

  const shareClassName = useMemo(() => {
    if (typeof offer.shareClass === 'string') {
      return '';
    }
    return (offer.shareClass as HoldingClass)?.name || '';
  }, [offer.shareClass]);

  const totalAmount = offer.sharesCount.total * offer.sharePrice;

  const progressPercent = useMemo(() => {
    if (offer.sharesCount.total === 0) return 0;
    return Math.round(
      (offer.sharesCount.agreed / offer.sharesCount.total) * 100
    );
  }, [offer.sharesCount]);

  const status = getOfferStatus(offer);
  const statusConfig = tradingOfferStatus[status];

  const isPartialOffer =
    offer.isPartialAmountEnabled && offer.sharesCount.agreed > 0;

  const handleClick = () => {
    onViewDetail(offer._id);
  };

  return (
    <div
      className={`nk-iv-scheme-item cursor-pointer hover-shadow hover-scale ${
        rowBorderColor[offer.type]
      }`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && handleClick()}
    >
      <div
        className={`nk-iv-scheme-icon ${
          status === 'ACTIVE' ? 'is-running' : ''
        }`}
        title={isSellOffer ? t('Sale') : t('Purchase')}
      >
        <em
          className={`icon ni ${
            isSellOffer ? 'ni-wallet-out' : 'ni-wallet-in'
          }`}
        />
      </div>

      <div className="nk-iv-scheme-info nk-iv-scheme-info-stack text-start">
        <div className="nk-iv-scheme-block-left">
          <div className="nk-iv-scheme-name">
            {formatNumber(offer.sharesCount.total)} {t('Shares').toLowerCase()}
            <TradingOfferPartialityIcon
              id={offer._id}
              isPartial={isPartialOffer}
            />
          </div>

          {isShareClassVisible() && (
            <div className="nk-iv-scheme-desc text-soft">
              {t('Class')}:&nbsp;
              <span className="text-dark">{shareClassName || '-'}</span>
            </div>
          )}
        </div>

        <div className="nk-iv-scheme-block-right">
          <div className="nk-iv-scheme-desc">
            {t('PricePerShare')}:&nbsp;
            <span className="amount">{formatCurrency(offer.sharePrice)}</span>
          </div>
          <div className="nk-iv-scheme-desc text-soft">
            {t('Total')}:&nbsp;
            <span className="amount">{formatCurrency(totalAmount)}</span>
          </div>
        </div>
      </div>

      <div className="nk-iv-scheme-term">
        <div className="nk-iv-scheme-start nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">{t('Start')}</span>
          <span className="nk-iv-scheme-value date text-dark">
            {format(new Date(offer.startDate), dateFormat)}
          </span>
        </div>
        <div className="nk-iv-scheme-end nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">{t('End')}</span>
          <span className="nk-iv-scheme-value date text-dark">
            {format(new Date(offer.endDate), dateFormat)}
          </span>
        </div>
      </div>

      <div className="nk-iv-scheme-amount">
        <div className="nk-iv-scheme-amount-a nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">
            {isSellOffer ? t('Seller') : t('Buyer')}
          </span>
          <span className="nk-iv-scheme-value text-dark">{partnerName}</span>
        </div>
        <div className="nk-iv-scheme-amount-b nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">{t('Status')}</span>
          <TradingOfferStatusBadge status={status} />
        </div>
      </div>

      <div className="nk-iv-scheme-more">
        <button
          type="button"
          className="btn btn-icon btn-lg btn-round btn-trans"
          title={t('ViewDetail')}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          <em className="icon ni ni-forward-ios" />
        </button>
      </div>

      <div className="nk-iv-scheme-progress">
        <div
          className={`progress-bar ${statusConfig.fill}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
};

export default TradingOfferRow;
